import { call, select } from 'redux-saga/effects';
import { requestPrecPlantingSeasons } from '../requesters/precisionPlantingRequester';
import { DMB_CROP_SEASON_FILTER } from '../constants/featureFlags';
import { FeatureFlagKey } from '../types/FeatureFlag';
import { getApplicationTreatments } from './applicationTreatmentSaga';
import { AppState } from '../store/types/Store';
import { ApplicationsBySeason } from '../actions/applicationTreatments/types/state';
import { selectApplicationCardEnabled } from './applicationTreatment/utils';

interface SeasonCropInfo {
  seasonTag: string;
  crop?: string;
  plantingEndDate?: string;
  plantingStartDate?: string;
  harvestEndDate?: string;
  harvestStartDate?: string;
  liquidStartDate?: number;
  liquidEndDate?: number;
}

interface FieldSummary {
  seasonCropInfos?: SeasonCropInfo[];
}

interface FvPlusResult {
  fieldSummaries?: FieldSummary[];
}

interface SeasonEntry {
  season: string;
  applicationData: unknown[];
  plantingEndDate?: string;
  plantingStartDate?: string;
  plantingYear?: number;
  harvestEndDate?: string;
  harvestStartDate?: string;
  harvestYear?: number;
  cropCustomName?: string;
  hasApplicationData?: boolean; // Flag to indicate if season has real application data
}

interface BasicSeasonEntry {
  season: string;
  plantingEndDate?: string;
  plantingStartDate?: string;
  plantingYear?: number;
  harvestEndDate?: string;
  harvestStartDate?: string;
  harvestYear?: number;
  cropCustomName?: string;
}

interface LiquidSeason {
  crop: string;
  cropName: string;
  liquidEndDate?: number;
  liquidStartDate?: number;
  liquidYear?: number;
  year: number;
  seasonTag?: string;
  seasonCrop?: string;
  seasonSeason?: string;
}

interface PrecisionPlantingResult {
  seasonList: SeasonEntry[];
  liquidSeasons: LiquidSeason[];
}

export const useCropSeasonFilterSelector = (state: AppState): boolean => 
  state.feature?.userConfigFeatures?.[DMB_CROP_SEASON_FILTER]?.value ?? false;

// application treatments will be fetched when either crop-season filter or application card is enabled
export const useTreatmentsRequiredSelector = (state: AppState): boolean => {
  const cropSeasonFilter = state.feature?.userConfigFeatures?.[DMB_CROP_SEASON_FILTER]?.value ?? false;
  const applicationCard = state.feature?.userConfigFeatures?.[FeatureFlagKey.DMB_APPLICATION_CARD]?.value ?? false;
  return Boolean(cropSeasonFilter || applicationCard);
};

export function* getPrecPlantingSeasonsOL(fieldUUID: string): Generator<unknown, PrecisionPlantingResult, unknown> {
  try {
    const applicationCardEnabled = yield select(selectApplicationCardEnabled);

    const fvPlusResult = (yield call(requestPrecPlantingSeasons, fieldUUID)) as FvPlusResult;

    const allSeasonCropInfos: SeasonCropInfo[] =
      fvPlusResult?.fieldSummaries?.[0]?.seasonCropInfos ?? [];
    
    let liquidSeasonCropInfos: SeasonCropInfo[] = [];
    if (!applicationCardEnabled) {
      liquidSeasonCropInfos = allSeasonCropInfos.filter(season => 'liquidStartDate' in season);
    }

    const seasonCropInfos = allSeasonCropInfos.filter(season => !('liquidStartDate' in season));

    const seasonTagsSet = new Set<string>();
    allSeasonCropInfos.forEach((season) => {
      seasonTagsSet.add(season.seasonTag);
    });

    const applicationDataBySeason = (yield call(getApplicationTreatments, fieldUUID)) as ApplicationsBySeason;
    const plantingHarvestSeasonsMap: Record<string, SeasonCropInfo> = {};
    seasonCropInfos.forEach((season) => {
      plantingHarvestSeasonsMap[season.seasonTag] = season;
    });

    const allSeasonTags = Array.from(
      new Set([
        ...Object.keys(plantingHarvestSeasonsMap),
        ...Object.keys(applicationDataBySeason),
      ]),
    );

    const seasonList = allSeasonTags.reduce<SeasonEntry[]>((acc, seasonTag) => {
      const plantingHarvestSeason = plantingHarvestSeasonsMap[seasonTag];
      const applicationData = applicationDataBySeason[seasonTag] || [];

      const entry: SeasonEntry = {
        season: seasonTag,
        applicationData,
      };

      if (plantingHarvestSeason) {
        if ('plantingEndDate' in plantingHarvestSeason && plantingHarvestSeason.plantingEndDate) {
          entry.plantingEndDate = plantingHarvestSeason.plantingEndDate;
          entry.plantingStartDate = plantingHarvestSeason.plantingStartDate;
          entry.plantingYear = new Date(plantingHarvestSeason.plantingEndDate).getFullYear();
        }
        if ('harvestEndDate' in plantingHarvestSeason && plantingHarvestSeason.harvestEndDate) {
          entry.harvestEndDate = plantingHarvestSeason.harvestEndDate;
          entry.harvestStartDate = plantingHarvestSeason.harvestStartDate;
          entry.harvestYear = new Date(plantingHarvestSeason.harvestEndDate).getFullYear();
        }
        entry.cropCustomName = plantingHarvestSeason.crop;
      } else if (Array.isArray(applicationData) && applicationData.length > 0) {
        let cropCustomName = '';
        const firstApp = applicationData[0] as { passName?: string; productStats?: { name?: string }[] };
        const appWithNamedProduct = applicationData.find(
          (app: unknown) => {
            const typedApp = app as { productStats?: { name?: string }[] };
            return typedApp.productStats?.find((p: { name?: string }) => p.name);
          }
        );
        if (appWithNamedProduct) {
          const typedApp = appWithNamedProduct as { productStats: { name?: string }[] };
          const namedProduct = typedApp.productStats.find((p: { name?: string }) => p.name);
          if (namedProduct) {
            cropCustomName = namedProduct.name || '';
          }
        } else if (firstApp?.passName) {
          cropCustomName = firstApp.passName;
        }
        entry.cropCustomName = cropCustomName;
      }
      // Validate if this season has real application data
      const hasRealApplicationData = Array.isArray(entry.applicationData) && 
        entry.applicationData.length > 0 &&
        entry.applicationData.some((app: unknown) => {
          const typedApp = app as { passName?: string; productStats?: unknown[] };
          const hasPassName = Boolean(typedApp.passName);
          const hasProductStats = Boolean(typedApp.productStats && typedApp.productStats.length > 0);
          return hasPassName || hasProductStats;
        });
      const hasPlantingHarvestData = entry.plantingEndDate || entry.harvestEndDate;
      
      // Include seasons with planting/harvest data but mark them with hasApplicationData flag
      if (hasPlantingHarvestData || hasRealApplicationData) {
        entry.hasApplicationData = hasRealApplicationData;
        acc.push(entry);
      }
      return acc;
    }, []);

    // Create liquid seasons from precision planting liquid data and application data
    const liquidSeasons: LiquidSeason[] = [
      // Liquid seasons from precision planting mock data (for mock mode)
      ...liquidSeasonCropInfos.map((season) => {
        const { seasonTag } = season;
        const [year, seasonCrop, , , seasonSeason] = seasonTag.split(':');

        return {
          crop: encodeURIComponent(season.crop || ''),
          cropName: season.crop || '',
          liquidEndDate: season.liquidEndDate,
          liquidStartDate: season.liquidStartDate,
          liquidYear: season.liquidEndDate ? new Date(season.liquidEndDate).getFullYear() : undefined,
          year: parseInt(year, 10),
          seasonTag,
          seasonCrop,
          seasonSeason,
        };
      }),
      // Liquid seasons from application data (for real data)
      ...seasonList.flatMap((season) =>
        (season.applicationData || []).map((app: unknown) => {
          const typedApp = app as {
            passName?: string;
            endDateTime?: string;
            startDateTime?: string;
          };
          const { season: seasonTag } = season;
          const [year, seasonCrop, , , seasonSeason] = seasonTag.split(':');

          return {
            crop: encodeURIComponent(typedApp.passName || ''),
            cropName: typedApp.passName || '',
            liquidEndDate: typedApp.endDateTime ? new Date(typedApp.endDateTime).getTime() : undefined,
            liquidStartDate: typedApp.startDateTime ? new Date(typedApp.startDateTime).getTime() : undefined,
            liquidYear: typedApp.endDateTime ? new Date(typedApp.endDateTime).getFullYear() : undefined,
            year: parseInt(year, 10),
            seasonTag,
            seasonCrop,
            seasonSeason,
          };
        })
      )
    ];

    return { seasonList, liquidSeasons };
  } catch (error) {
    return { seasonList: [], liquidSeasons: [] };
  }
}

export function* getPrecPlantingSeasons(fieldUUID: string): Generator<unknown, PrecisionPlantingResult | { seasonList: null } | { seasonList: BasicSeasonEntry[] } | { seasonList: BasicSeasonEntry[]; liquidSeasons: LiquidSeason[] }, unknown> {
  const shouldFetchTreatments = (yield select(useTreatmentsRequiredSelector)) as boolean;
  if (shouldFetchTreatments) {
    const result = (yield call(getPrecPlantingSeasonsOL, fieldUUID)) as PrecisionPlantingResult;
    return result;
  }

  const response = (yield call(requestPrecPlantingSeasons, fieldUUID)) as FvPlusResult;

  if (response && response.fieldSummaries && response.fieldSummaries.length) {
    const { seasonCropInfos } = response.fieldSummaries[0];

    if (!seasonCropInfos) {
      return {
        seasonList: [],
      };
    }

    const seasonList = seasonCropInfos.reduce<BasicSeasonEntry[]>((acc, season) => {
      if (
        !season.seasonTag ||
        (!season.plantingEndDate && !season.harvestEndDate)
      ) {
        return acc;
      }

      const entry: BasicSeasonEntry = {
        cropCustomName: season.crop,
        season: season.seasonTag,
      };

      if ('plantingEndDate' in season && season.plantingEndDate) {
        entry.plantingEndDate = season.plantingEndDate;
        entry.plantingStartDate = season.plantingStartDate;
        entry.plantingYear = new Date(season.plantingEndDate).getFullYear();
      }
      if ('harvestEndDate' in season && season.harvestEndDate) {
        entry.harvestEndDate = season.harvestEndDate;
        entry.harvestStartDate = season.harvestStartDate;
        entry.harvestYear = new Date(season.harvestEndDate).getFullYear();
      }

      return [...acc, entry];
    }, []);

    const liquidSeasons: LiquidSeason[] = seasonCropInfos
      .filter((season): season is SeasonCropInfo & { liquidEndDate: number } => 
        typeof season.liquidEndDate === 'number'
      )
      .map((season) => ({
        crop: encodeURIComponent(encodeURIComponent(season.crop || '')),
        cropName: season.crop || '',
        liquidEndDate: season.liquidEndDate,
        liquidStartDate: season.liquidStartDate,
        liquidYear: new Date(season.liquidEndDate).getFullYear(),
        year: parseInt((season.seasonTag || '').split(':')[0], 10),
      }));

    return { seasonList, liquidSeasons };
  }

  return {
    seasonList: null,
  };
}
